import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, X } from 'lucide-react'
import { MODULE_META, ModuleKey } from '../lib/moduleMeta'
import { AccessLevel, ALL_LEVELS, levelLabelKey } from '../lib/levelMeta'

interface Props {
  selectedCount: number
  running: boolean
  onApply: (module: ModuleKey, level: AccessLevel | null) => void
  onClearSelection: () => void
}

const MembersBulkBar: React.FC<Props> = ({ selectedCount, running, onApply, onClearSelection }) => {
  const { t } = useTranslation()
  const [module, setModule] = useState<ModuleKey | ''>('')
  const [level, setLevel] = useState<AccessLevel | 'RESET' | ''>('')

  if (selectedCount === 0) return null

  const canApply = module !== '' && level !== ''
  const apply = () => {
    if (!canApply) return
    onApply(module as ModuleKey, level === 'RESET' ? null : (level as AccessLevel))
  }

  return (
    <div className="sticky top-0 z-10 bg-blue-50 border-y border-blue-200 px-3 py-2 flex items-center gap-2 flex-wrap text-sm">
      <span className="font-medium text-blue-900">{t('Members_selected_count', { count: selectedCount })}</span>

      <Select value={module} onValueChange={(v) => setModule(v as ModuleKey)} disabled={running}>
        <SelectTrigger className="h-8 w-[170px] bg-background"><SelectValue placeholder={t('Members_bulk_set_level')} /></SelectTrigger>
        <SelectContent>
          {MODULE_META.map((m) => (
            <SelectItem key={m.key} value={m.key}>{t(m.labelKey, m.key)}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={level} onValueChange={(v) => setLevel(v as AccessLevel | 'RESET')} disabled={running || module === ''}>
        <SelectTrigger className="h-8 w-[150px] bg-background"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {ALL_LEVELS.map((lvl) => (
            <SelectItem key={lvl} value={lvl}>{t(levelLabelKey(lvl))}</SelectItem>
          ))}
          <SelectItem value="RESET">{t('Members_bulk_reset')}</SelectItem>
        </SelectContent>
      </Select>

      <Button size="sm" className="h-8" onClick={apply} disabled={!canApply || running}>
        {running && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('apply', 'Apply')}
      </Button>

      <Button variant="ghost" size="sm" className="h-8 ml-auto" onClick={onClearSelection} disabled={running}>
        <X className="h-3.5 w-3.5" />{t('Members_bulk_clear_selection')}
      </Button>
    </div>
  )
}

export default MembersBulkBar
